import React from 'react';
import { useTranslation } from 'react-i18next';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import { useBudget } from '../../context/BudgetContext';
import { formatPercent, formatCAD } from '../../utils/formatCurrency';

export default function SavingsTargetCard() {
  const { t } = useTranslation();
  const { state, derived } = useBudget();

  const target = state.goals.savingsTargetPct || 20;
  const current = derived.savingsRate;
  const progressPct = target > 0 ? Math.min(100, (current / target) * 100) : 0;

  const color = current >= target ? '#2E7D32' : current >= target * 0.5 ? '#F9A825' : '#C62828';

  return (
    <Card elevation={1} sx={{ height: '100%', border: '1px solid #E6E9EE', transition: 'transform 0.2s ease, box-shadow 0.2s ease', '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 } }}>
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.25, color: '#1E3A5F' }}>
          {t('budgetPlanner.goals.savingsCard.title', 'Meta de ahorro')}
        </Typography>

        <Box sx={{ textAlign: 'center', mb: 1.5 }}>
          <Typography variant="h4" fontWeight={700} sx={{ color }}>
            {formatPercent(current)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('budgetPlanner.goals.savingsCard.of', 'de')} {formatPercent(target)} {t('budgetPlanner.goals.savingsCard.target', 'meta')}
          </Typography>
        </Box>

        <LinearProgress
          variant="determinate"
          value={progressPct}
          sx={{
            height: 12,
            borderRadius: 6,
            bgcolor: '#E6E9EE',
            '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 6 },
            mb: 1,
          }}
        />

        <Typography variant="caption" color="text.secondary" textAlign="center" display="block">
          {current >= target
            ? t('budgetPlanner.goals.savingsCard.achieved', 'Meta alcanzada')
            : t('budgetPlanner.goals.savingsCard.progress', '{{pct}}% del camino', { pct: Math.round(progressPct) })}
        </Typography>

        {/* Micro-insights */}
        <Box sx={{ mt: 1.5 }}>
          {current < target ? (
            <Typography sx={{ fontSize: 12, color: '#5F6B7A', lineHeight: 1.5 }}>
              Necesitas ahorrar {formatCAD(Math.max(0, (target / 100) * derived.monthlyIncome - derived.monthlyNet))}/mes más para alcanzar tu meta de {target}%.
            </Typography>
          ) : (
            <Typography sx={{ fontSize: 12, color: '#5F6B7A', lineHeight: 1.5 }}>
              Estás cumpliendo tu meta de ahorro mensual.
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}
